import {
  useClientApplicationQuery,
  useUpdateClientApplicationMutation,
} from "@graphql/client-applications/ClientApplications.g";
import { TenantedClientApplicationReviewStateChoices } from "@graphql/generated/graphql.g";
import { useMemo } from "react";
import { toast } from "react-toastify";

import {
  ClientApplicationApprovalReviewForm,
  ClientApplicationApprovalReviewFormValues,
} from "@components/Forms";

import { useNotification } from "@providers/NotificationProvider/useNotification";

type Props = {
  clientId: string;
  clientApplicationId: string;
};

export const UpdateClientApplicationApprovalForm = ({
  clientId,
  clientApplicationId,
}: Props) => {
  const clientApplicationQuery = useClientApplicationQuery({
    clientId,
    clientApplicationId,
  });
  const updateClientApplication = useUpdateClientApplicationMutation();
  const updateApp = updateClientApplication.mutateAsync;
  const { invalidateNotifications } = useNotification();

  const clientApplication = clientApplicationQuery.data?.clientApplication ?? undefined;

  const values = useMemo(() => {
    return {
      reviewState:
        clientApplication?.reviewState ??
        TenantedClientApplicationReviewStateChoices.Discovered,
      managedBy: clientApplication?.managedBy,
      supportedBy: clientApplication?.supportedBy,
      businessOwnerId: clientApplication?.businessOwner?.id,
      clientId: clientId,
      notes: clientApplication?.notes,
    };
  }, [clientId, clientApplication]);

  const onSubmit = async (
    values: ClientApplicationApprovalReviewFormValues,
  ) => {
    if (!clientApplication) return;

    try {
      const { updateClientApplication } = await updateApp({
        input: {
          id: clientApplication.id,
          ...values,
        },
      });

      if (updateClientApplication?.errors) {
        updateClientApplication?.errors.forEach((error) => {
          throw Error(error?.message || "Unknown error");
        });
      }

      await clientApplicationQuery.refetch();
      
      // Invalidate notification cache since backend resolves notifications when reviewState changes
      invalidateNotifications();

      toast.success("Application reviewed");
    } catch (error) {
      toast.error("Unable to update application approval state");
      console.error(error);
    }
  };

  return (
    <ClientApplicationApprovalReviewForm
      onSubmit={onSubmit}
      values={values}
      submitText="Update"
    />
  );
};
